import { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import ReactSelect from 'react-select'
import colors from 'components/utils/colors'
import { useTranslation } from 'react-i18next'

import SearchInput from 'components/Shared/SearchInput/Index'
import Option from 'components/Shared/Select/components/Option'
import DropdownIndicator from 'components/Shared/InlineSelect/components/DropdownIndicator'

import SelectControl from './components/SelectControl'
import ValueContainer from './components/ValueContainer'
import Menu from './components/Menu'
import SingleValue from './components/SingleValue'

const SearchByWrapper = styled.div`
  margin-top: 0;
  display: flex;
  align-items: center;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${colors.secondary20};
  margin-right: 4px;
`

const StyledSelect = styled(ReactSelect)`
  margin-right: 6px;
`

const SearchBy = ({
  searchParams, defaultSearchParam, defaultSearchValue, onSubmit, placeholder, className,
}) => {
  const { t } = useTranslation()

  const [searchParam, setSearchParam] = useState(searchParams ? (defaultSearchParam || searchParams[0]) : null)
  const [searchValue, setSearchValue] = useState(defaultSearchValue || '')
  // see https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops
  const [prevSearchParams, setPrevSearchParam] = useState(null)

  if (searchParams !== prevSearchParams) {
    setPrevSearchParam(searchParams)
    setSearchParam(defaultSearchParam || searchParams[0])
    setSearchValue(defaultSearchValue || '')
  }

  const onSearchParamChange = (searchParam_) => {
    setSearchParam(searchParam_.value)
  }

  const onValueChange = (event) => {
    setSearchValue(event.target.value)
  }

  const onKeyPress = (event) => {
    if (event.key === 'Enter') {
      submit()
    }
  }

  const onSubmit_ = () => {
    submit()
  }

  const submit = () => {
    const params = {
      searchValue: searchValue,
      searchParam: searchParam,
    }
    onSubmit(params)
  }

  const clearSearch = () => {
    setSearchValue('')

    if (defaultSearchValue) {
      const params = {
        searchValue: '',
        searchParam: searchParam,
      }
      onSubmit(params)
    }
  }

  const components = {
    IndicatorSeparator: () => null,
    Control: SelectControl,
    ValueContainer: ValueContainer,
    Menu: Menu,
    DropdownIndicator: DropdownIndicator,
    SingleValue: SingleValue,
    Option: Option,
  }

  let searchParamsOptions = []
  if (searchParams) {
    searchParamsOptions = searchParams.map(searchParam => ({
      value: searchParam,
      label: searchParam,
    }))
  }
  const selectedSearchParamOption = searchParamsOptions.find(e => e.value === searchParam)

  return (
    <SearchByWrapper data-test='search-by' className={className}>
      {searchParam && (
        <>
          <Title>{t('searchBy.title')}:</Title>
          <StyledSelect
            value={selectedSearchParamOption}
            options={searchParamsOptions}
            onChange={onSearchParamChange}
            classNamePrefix='react-select'
            components={components}
            isSearchable={false}
          />
        </>
      )}
      <SearchInput
        placeholder={placeholder || t('searchBy.placeholder')}
        value={searchValue}
        onChange={onValueChange}
        onClear={clearSearch}
        onSearch={onSubmit_}
        onKeyPress={onKeyPress}
      />
    </SearchByWrapper>
  )
}

SearchBy.propTypes = {
  searchParams: PropTypes.array,
  defaultSearchParam: PropTypes.string,
  defaultSearchValue: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
}

export default SearchBy
